import { TextInputField } from "@sainsburys-tech/luna-form";
import { Form } from "@sainsburys-tech/luna-form";
import { Container, GridItem, GridWrapper } from "@sainsburys-tech/luna-grid";
import { FC, useRef } from "react";
import {
  DimensionLengthUOMOptions,
  DimensionWeightUOMOptions,
  DimensionVolumeUOMOptions,
  FIELDS,
  FormInputLevels,
} from "../../../Interfaces/Product/IProductFormField";
import { ProductFormProps } from "../../../Types/Product/ProductFormProps";
import ActionButton from "../../SharedComponents/ActionButton";
import { useSelector } from "react-redux";
import { Heading6 } from "@sainsburys-tech/luna-typography";
import { SelectField } from "@sainsburys-tech/luna-form";
import { UI_CONSTANTS } from "../../../Constants/constants";
import { ProductValueType } from "../../../Types/Product/ProductValueType";
import { ProductError } from "../../../Types/Product/ProductError";
import { ProductWarning } from "../../../Types/Product/ProductWarning";
import { packSizeToDisplay } from "./FormHelper/productValueTypeFields";
import ApprovedExceptionCheckbox from "../../SharedComponents/ApprovedExceptionCheckbox";


const MeasurementsForm: FC<ProductFormProps> = (props) => {
  const values = props.values as ProductValueType;
  const errors: ProductError | null | undefined = props.errors && props.errors as ProductError;
  const warnings: ProductWarning | null | undefined = props.warnings && props.warnings as ProductWarning;

  const payload = useSelector((state: any) => state.popupReducer);

  const initialInnerDimensions = useRef({
    length: values.innerDimensions?.length,
    width: values.innerDimensions?.width,
    height: values.innerDimensions?.height,
  });

  const initialOuterDimensions = useRef({
    length: values.outerDimensions?.length,
    width: values.outerDimensions?.width,
    height: values.outerDimensions?.height,
  });

  const numToStringReplacer = (key: any, value: any) => {
    if (typeof value === "number") {
      return value?.toString();
    }
    return value;
  };

  const isInitialState =
    JSON.stringify(payload?.data?.editPayload?.values, numToStringReplacer) ===
    JSON.stringify(values, numToStringReplacer);

  const calculateVolume = (length: number | undefined, width: number | undefined, height: number | undefined): number => {
    if (length && width && height && length > 0 && width > 0 && height > 0) {
      return ((length * width * height) / 1000000);
    }
    return 0;
  };

  const hasDimensionChanged = (
    currentDimensions:
      | { length?: number; width?: number; height?: number }
      | undefined,
    initialDimensions: { length?: number; width?: number; height?: number },
  ) => {
    return (
      currentDimensions?.length !== initialDimensions.length ||
      currentDimensions?.width !== initialDimensions.width ||
      currentDimensions?.height !== initialDimensions.height
    );
  };

  const getCalculatedVolume = (
    currentDimensions: { length?: number; width?: number; height?: number; volume?: number } | undefined,
    initialDimensions: { length?: number; width?: number; height?: number }
  ): number => {
    const { length, width, height, volume } = currentDimensions || {};
    if (hasDimensionChanged(currentDimensions, initialDimensions)) {
      return calculateVolume(length, width, height);
    }
    return volume ?? 0;
  };

  const innerVolume = getCalculatedVolume(values.innerDimensions, initialInnerDimensions.current);
  const outerVolume = getCalculatedVolume(values.outerDimensions, initialOuterDimensions.current);

  return (
    <Container>
      <Form className="o-form">
        <GridWrapper className="ln-u-push-bottom">
          <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
            <TextInputField
              name={FIELDS.sainId.name}
              label={FormInputLevels.SainId}
              maxLength={128}
              value={values.sainId}
              disabled
            />
          </GridItem>
          <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
            <TextInputField
              name="displayItemNumber"
              label={FormInputLevels.DisplayItemNumber}
              value={
                values.skuNumber
                  ? values.skuNumber
                  : values.catNumber
              }
              disabled={true}
            />
          </GridItem>
          <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
            <TextInputField
              name={FIELDS.description.name}
              label={FormInputLevels.Description}
              maxLength={128}
              value={values.description}
              disabled={true}
            />
          </GridItem>
          <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
            <TextInputField
              name={FIELDS.packSize.name}
              label={FormInputLevels.PackSize}
              value={packSizeToDisplay(values)}
              disabled
            />
          </GridItem>
        </GridWrapper>
        {values.alias !== UI_CONSTANTS.ARGOS && (
          <>
            <GridWrapper className="ln-u-push-bottom">
              <GridItem size={{ md: "1/2", sm: "2/3", xs: "1" }}>
                <div className="measurementsForm-breakpack-dimensions">
                  <Heading6 element="div">Breakpack Dimensions</Heading6>
                  <input
                    className="checkbox-indicator"
                    type="checkbox"
                    name={FIELDS.breakPackIndicator.name}
                    defaultChecked={values.breakPackIndicator}
                    onClick={(e: any) => {
                      props.onChange(e, "", "", true);
                    }}
                  />
                </div>
              </GridItem>
            </GridWrapper>
            <GridWrapper className="ln-u-push-bottom">
              <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
                <TextInputField
                  name={FIELDS.outerDimensions.ti.name}
                  label={FormInputLevels.OuterTi}
                  maxLength={128}
                  value={values.outerDimensions?.ti}
                  error={errors?.outerDimensions?.ti}
                  onBlur={props.onBlur}
                  onChange={props.onChange}
                  disabled={!values.breakPackIndicator}
                />
              </GridItem>
              <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
                <TextInputField
                  name={FIELDS.outerDimensions.hi.name}
                  label={FormInputLevels.OuterHi}
                  maxLength={128}
                  value={values.outerDimensions?.hi}
                  error={errors?.outerDimensions?.hi}
                  onBlur={props.onBlur}
                  onChange={props.onChange}
                  disabled={!values.breakPackIndicator}
                />
              </GridItem>
              <GridItem size={{ md: "1/3", sm: "1/3", xs: "1" }}>
                <TextInputField
                  name={FIELDS.outerDimensions.breakPackQty.name}
                  label={FormInputLevels.OuterQty}
                  maxLength={128}
                  value={values.outerDimensions?.breakPackQty}
                  error={errors?.outerDimensions?.breakPackQty}
                  onBlur={props.onBlur}
                  onChange={props.onChange}
                  disabled={!values.breakPackIndicator}
                />
              </GridItem>
              <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
                <TextInputField
                  name={FIELDS.outerDimensions.length.name}
                  label={FormInputLevels.OuterLength}
                  maxLength={128}
                  value={values.outerDimensions?.length}
                  error={errors?.outerDimensions?.length}
                  warning={
                    errors?.outerDimensions?.length
                      ? ""
                      : warnings?.outerDimensions?.length
                  }
                  onBlur={props.onBlur}
                  onChange={props.onChange}
                  disabled={!values.breakPackIndicator}
                  required
                />
              </GridItem>
              <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
                <SelectField
                  name={FIELDS.outerDimensions.lengthUOM.name}
                  label={FormInputLevels.OuterLengthUOM}
                  options={DimensionLengthUOMOptions}
                  defaultValue={values.outerDimensions?.lengthUOM}
                  onChange={props.onChange}
                  onBlur={props.onBlur}
                  disabled={!values.breakPackIndicator}
                  placeholder={false}
                />
              </GridItem>
              <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
                <TextInputField
                  name={FIELDS.outerDimensions.width.name}
                  label={FormInputLevels.OuterWidth}
                  maxLength={128}
                  value={values.outerDimensions?.width}
                  error={errors?.outerDimensions?.width}
                  warning={
                    errors?.outerDimensions?.width
                      ? ""
                      : warnings?.outerDimensions?.width
                  }
                  onBlur={props.onBlur}
                  onChange={props.onChange}
                  disabled={!values.breakPackIndicator}
                  required
                />
              </GridItem>
              <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
                <SelectField
                  name={FIELDS.outerDimensions.widthUOM.name}
                  label={FormInputLevels.OuterWidthUOM}
                  options={DimensionLengthUOMOptions}
                  defaultValue={values.outerDimensions?.widthUOM}
                  onChange={props.onChange}
                  onBlur={props.onBlur}
                  disabled={!values.breakPackIndicator}
                  placeholder={false}
                />
              </GridItem>
              <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
                <TextInputField
                  name={FIELDS.outerDimensions.height.name}
                  label={FormInputLevels.OuterHeight}
                  maxLength={128}
                  value={values.outerDimensions?.height}
                  error={errors?.outerDimensions?.height}
                  onBlur={props.onBlur}
                  onChange={props.onChange}
                  disabled={!values.breakPackIndicator}
                  required
                />
              </GridItem>
              <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
                <SelectField
                  name={FIELDS.outerDimensions.heightUOM.name}
                  label={FormInputLevels.OuterHeightUOM}
                  options={DimensionLengthUOMOptions}
                  defaultValue={values.outerDimensions?.heightUOM}
                  onChange={props.onChange}
                  onBlur={props.onBlur}
                  disabled={!values.breakPackIndicator}
                  placeholder={false}
                />
              </GridItem>
              <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
                <TextInputField
                  name={FIELDS.outerDimensions.weight.name}
                  label={FormInputLevels.OuterWeight}
                  maxLength={128}
                  value={values.outerDimensions?.weight}
                  error={errors?.outerDimensions?.weight}
                  onBlur={props.onBlur}
                  onChange={props.onChange}
                  disabled={!values.breakPackIndicator}
                  required
                />
              </GridItem>
              <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
                <SelectField
                  name={FIELDS.outerDimensions.weightUOM.name}
                  label={FormInputLevels.OuterWeightUOM}
                  options={DimensionWeightUOMOptions}
                  defaultValue={values.outerDimensions?.weightUOM}
                  onChange={props.onChange}
                  onBlur={props.onBlur}
                  disabled={!values.breakPackIndicator}
                  placeholder={false}
                />
              </GridItem>
              {values.familyGroup === "Food" && (
                <>
                  <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
                    <TextInputField
                      name={FIELDS.outerDimensions.volume.name}
                      label={FormInputLevels.OuterVolume}
                      value={values.breakPackIndicator && outerVolume > 0 ? outerVolume.toString() : ""}
                      onChange={props.onChange}
                      disabled
                    />
                  </GridItem>
                  <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
                    <SelectField
                      name={FIELDS.outerDimensions.volumeUOM.name}
                      label={FormInputLevels.OuterVolumeUOM}
                      options={DimensionVolumeUOMOptions}
                      defaultValue={values.outerDimensions?.volumeUOM}
                      onChange={props.onChange}
                      onBlur={props.onBlur}
                      disabled
                      placeholder={false}
                    />
                  </GridItem>
                </>
              )}
            </GridWrapper>
          </>
        )}
        <Heading6 element="div">
          {values.alias == UI_CONSTANTS.ARGOS ? "" : "Case "}Dimensions
        </Heading6>
        <GridWrapper className="ln-u-push-bottom">
          <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
            <TextInputField
              name={FIELDS.innerDimensions.ti.name}
              label={FormInputLevels.InnerTi}
              maxLength={128}
              value={values.innerDimensions?.ti}
              error={errors?.innerDimensions?.ti}
              onBlur={props.onBlur}
              onChange={props.onChange}
              required
            />
          </GridItem>
          <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
            <TextInputField
              name={FIELDS.innerDimensions.hi.name}
              label={FormInputLevels.InnerHi}
              maxLength={128}
              value={values.innerDimensions?.hi}
              error={errors?.innerDimensions?.hi}
              onBlur={props.onBlur}
              onChange={props.onChange}
              required
            />
          </GridItem>
          <GridItem size={{ md: "1/3", sm: "1/3", xs: "1" }}>
            <TextInputField
              name={'casesPerPallet'}
              label={FormInputLevels.CasesPerPallet}
              value={((values?.innerDimensions?.ti ?? 0) * (values?.innerDimensions?.hi ?? 0)) || ""}
              disabled
            />
          </GridItem>
          <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
            <TextInputField
              name={FIELDS.innerDimensions.length.name}
              label={FormInputLevels.InnerLength}
              maxLength={128}
              value={values.innerDimensions?.length}
              error={errors?.innerDimensions?.length}
              onBlur={props.onBlur}
              onChange={props.onChange}
              required
            />
          </GridItem>
          <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
            <SelectField
              name={FIELDS.innerDimensions.lengthUOM.name}
              label={FormInputLevels.InnerLengthUOM}
              options={DimensionLengthUOMOptions}
              defaultValue={values.innerDimensions?.lengthUOM}
              onChange={props.onChange}
              onBlur={props.onBlur}
              placeholder={false}
            />
          </GridItem>
          <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
            <TextInputField
              name={FIELDS.innerDimensions.width.name}
              label={FormInputLevels.InnerWidth}
              maxLength={128}
              value={values.innerDimensions?.width}
              error={errors?.innerDimensions?.width}
              onBlur={props.onBlur}
              onChange={props.onChange}
              required
            />
          </GridItem>
          <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
            <SelectField
              name={FIELDS.innerDimensions.widthUOM.name}
              label={FormInputLevels.InnerWidthUOM}
              options={DimensionLengthUOMOptions}
              defaultValue={values.innerDimensions?.widthUOM}
              onChange={props.onChange}
              onBlur={props.onBlur}
              placeholder={false}
            />
          </GridItem>
          <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
            <TextInputField
              name={FIELDS.innerDimensions.height.name}
              label={FormInputLevels.InnerHeight}
              maxLength={128}
              value={values.innerDimensions?.height}
              error={errors?.innerDimensions?.height}
              onBlur={props.onBlur}
              onChange={props.onChange}
              required
            />
          </GridItem>
          <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
            <SelectField
              name={FIELDS.innerDimensions.heightUOM.name}
              label={FormInputLevels.InnerHeightUOM}
              options={DimensionLengthUOMOptions}
              defaultValue={values.innerDimensions?.heightUOM}
              onChange={props.onChange}
              onBlur={props.onBlur}
              placeholder={false}
            />
          </GridItem>
          <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
            <TextInputField
              name={FIELDS.innerDimensions.weight.name}
              label={FormInputLevels.InnerWeight}
              maxLength={128}
              value={values.innerDimensions?.weight}
              error={errors?.innerDimensions?.weight}
              onBlur={props.onBlur}
              onChange={props.onChange}
              required
            />
          </GridItem>
          <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
            <SelectField
              name={FIELDS.innerDimensions.weightUOM.name}
              label={FormInputLevels.InnerWeightUOM}
              options={DimensionWeightUOMOptions}
              defaultValue={values.innerDimensions?.weightUOM}
              onChange={props.onChange}
              onBlur={props.onBlur}
              placeholder={false}
            />
          </GridItem>
          {values.merchandiseType === "Food" && (
            <>
              <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
                <TextInputField
                  name={FIELDS.innerDimensions.volume.name}
                  label={FormInputLevels.InnerVolume}
                  value={innerVolume > 0 ? innerVolume.toString() : ""}
                  onChange={props.onChange}
                  disabled
                />
              </GridItem>
              <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
                <SelectField
                  name={FIELDS.innerDimensions.volumeUOM.name}
                  label={FormInputLevels.InnerVolumeUOM}
                  options={DimensionVolumeUOMOptions}
                  defaultValue={values.innerDimensions?.volumeUOM}
                  onChange={props.onChange}
                  onBlur={props.onBlur}
                  disabled
                  placeholder={false}
                />
              </GridItem>
            </>
          )}
        </GridWrapper>
      </Form>
      {values.alias !== UI_CONSTANTS.ARGOS && values.merchandiseType === "Food" && (
        <GridWrapper className="ln-u-push-bottom">
          <GridItem size={{ md: "1/4", sm: "1/3", xs: "1" }}>
            <ApprovedExceptionCheckbox
              checked={values.overridenValidations}
              onChange={props.onChange}
            />
          </GridItem>
        </GridWrapper>
      )}
      <ActionButton
        onSubmit={props.onSubmit}
        onClearAll={props.onClearAll}
        onCancel={props.onCancel}
        isAddDisabled={isInitialState}
        isDirty={props.dirty}
        isEdit={props.isEdit}
        isClearButtonRequired={false}
        submitted={props.submitted}
      />
    </Container>
  );
};

export default MeasurementsForm;
